import { useRef, useEffect } from 'react';
import Highcharts from 'highcharts';
import { formatStatementAmount } from '../config/incomeStatementSankeyChartConfig';
import styles from './SankeyNodePopover.module.css';

const FONT_FAMILY = "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * Popover showing a pie chart breakdown of a clicked Sankey node's outbound
 * links. Rendered conditionally below the Sankey chart. Dismisses on Escape
 * or click-outside.
 */
const SankeyNodePopover = ({ node, breakdown, currency, onMouseEnter, onMouseLeave }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  // Render the pie chart imperatively into the container div.
  useEffect(() => {
    if (!containerRef.current || !breakdown || breakdown.length === 0) return;

    chartRef.current = Highcharts.chart(containerRef.current, {
      chart: {
        type: 'pie',
        height: 240,
        width: 320,
        style: { fontFamily: FONT_FAMILY },
        backgroundColor: 'transparent',
      },
      title: { text: node.name, style: { fontSize: '13px', fontWeight: '600' } },
      credits: { enabled: false },
      tooltip: {
        pointFormatter() {
          const amt = formatStatementAmount(this.y, currency);
          return `<b>${this.percentage.toFixed(1)}%</b> (${amt})`;
        },
      },
      plotOptions: {
        pie: {
          dataLabels: {
            format: '{point.name} ({point.percentage:.2f}%)',
            distance: 12,
            style: { fontSize: '10px', textOutline: 'none', fontWeight: '400' },
          },
        },
      },
      series: [{
        data: breakdown.map((b) => ({ name: b.name, y: b.weight, color: b.color })),
      }],
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [node, breakdown, currency]);

  const isTerminal = !breakdown || breakdown.length === 0;

  return (
    <div className={styles.popover} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {isTerminal ? (
        <div className={styles.terminal}>
          <strong>{node.name}</strong> is a terminal node (no further breakdown).
        </div>
      ) : (
        <div className={styles.chartContainer} ref={containerRef} />
      )}
    </div>
  );
};

export default SankeyNodePopover;
